import React, { useEffect, useState } from "react";
import "../styles/LoginPage.css";
// import logoImage from "../assets/images/new1.png";
import defaultLogo from "../assets/images/bklogo.png";
import { FaAndroid, FaApple, FaInfoCircle } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
// NavLink was imported but not used, so I'm removing it.
// import { NavLink } from "react-router-dom";
import { useNavigate, useSearchParams } from "react-router-dom"; // Import useSearchParams
import axios from "axios";
import { getLoginUser, clearLoginState } from "../redux/slices/getLoginSlice";
import {
  getProfileData,
  clearProfileDataSliceState,
} from "../redux/slices/profileSlice";
import { setShowMessage } from "../redux/slices/notificationSlice";

function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Get search params hook
  const [searchParams] = useSearchParams(); // Initialize useSearchParams. We don't need setSearchParams if using window.history

  const loginState = useSelector((state) => state.getLoginSlice.loginState);
  const profileDataState = useSelector((state) => state.profileData.profile);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // const [language, setLanguage] = useState("en"); // language state is declared but not used.
  // const currentLanguage = useSelector((state) => state.language.language); // currentLanguage is declared but not used.
  // const [logoImage, setLogoImage] = useState(null); // logoImage state declared but not used.
  const [agencyData, setAgencyData] = useState({
    agencyName: "",
    angencyLogo: null,
  });

  // Initialize state for login data
  const [loginData, setLoginData] = useState({
    username: "", // Corresponds to email or mobile
    password: "",
  });

  // State for build information tooltip
  const [showBuildInfo, setShowBuildInfo] = useState(false);
  const [buildInfo, setBuildInfo] = useState({
    buildId: process.env.REACT_APP_BUILD_ID || "Not available",
    branch: process.env.REACT_APP_BRANCH || "Not available",
    version: "Not available"
  });

  // Fetch version info from version.json if available
  useEffect(() => {

    const fetchVersionInfo = async () => {
      try {
        const response = await fetch('/version.json');
        if (response.ok) {
          const versionData = await response.json();
          const buildDateUTC = versionData.buildDate || "Not available";
          const humanReadableIST = buildDateUTC !== "Not available"
            ? new Date(buildDateUTC).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              weekday: "short",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              hour12: true
            })
            : "Not available";
          setBuildInfo(prevState => ({
            ...prevState,
            buildDate: humanReadableIST || "Not available",
            version: versionData.version || "Not available"
          }));
        }
      } catch (error) {
        console.error("Failed to load version information:", error);
      }
    };

    fetchVersionInfo();
  }, []);

  const validateLogoUri = async (logoUri) => {
    if (!logoUri) return null;

    try {
      const response = await axios.get(logoUri);
      if (response?.data?.message !== "Agency profile not found") {
        return logoUri;
      }
    } catch (err) {
      // Logo fetch failed or server error
      console.error("Error validating logo URI:", err); // Added console error for better debugging
    }

    return null;
  };

  // This useEffect handles populating fields from URL params and clearing them.
  // This is the block you provided.
  useEffect(() => {
    const emailFromUrl = searchParams.get("email");
    const passwordFromUrl = searchParams.get("password");

    let updatedState = {};
    let paramsFound = false; // Flag to know if we need to clear URL

    // Check if email param exists
    if (emailFromUrl) {
      updatedState.username = emailFromUrl;
      paramsFound = true;
    }

    // Check if password param exists
    if (passwordFromUrl) {
      updatedState.password = passwordFromUrl;
      paramsFound = true;
    }

    // If any param was found, update the state
    if (paramsFound) {
      setLoginData(prevData => ({
        ...prevData,
        ...updatedState // Apply the updates from URL params
      }));

      // --- Clear the URL parameters ---
      // Use replaceState to modify the current history entry without reloading
      // window.location.pathname gets the current path (e.g., "/login")
      window.history.replaceState({}, '', window.location.pathname);
      // The first argument is a state object (can be empty), second is title (unused by most browsers), third is the new URL.
    }
    // If params are not present, the initial state remains, and URL is not changed.
  }, [searchParams]); // Run when searchParams object changes (e.g. on initial load)
  // --- End of modified useEffect ---


  useEffect(() => {
    const retrieveAgencyProfile = async () => {
      const url = `${process.env.REACT_APP_NODE_URL}/api/agency/get-agencylogo`;

      setLoading(true);
      try {
        const response = await axios.get(url);

        if (response && response.data.result.length > 0) {
          const valueObj = response.data.result.reduce((acc, item) => {
            if (item?.key_name && item?.value !== undefined) {
              acc[item.key_name] = item.value;
            }
            return acc;
          }, {});

          const logoUri = valueObj.logo
            ? `${process.env.REACT_APP_NODE_URL}/api/agency/get-agency-profile/${valueObj.logo}`
            : null;

          const finalLogo = await validateLogoUri(logoUri);

          setAgencyData({
            agencyName: valueObj.name?.trim(),
            angencyLogo: finalLogo,
          });
        }
      } catch (err) {
        console.error("Error fetching agency profile:", err);
      }
      setLoading(false);
    };

    retrieveAgencyProfile();
  }, []);

  // This useEffect handles the "Remember me" feature from localStorage.
  // Modified to respect URL parameters and use functional state updates.
  useEffect(() => {
    // Check if email was provided in the URL parameters *in the current state of searchParams*.
    // If searchParams were just cleared, emailFromUrl here would be null in a subsequent run.
    const emailFromUrl = searchParams.get("email");

    // Only attempt to load remembered username if it wasn't provided via current URL params.
    if (!emailFromUrl) {
      const rememberedUsername = localStorage.getItem("rememberedUsername");
      if (rememberedUsername) {
        setLoginData(prevLoginData => {
          // Only set username from localStorage if it's not already populated
          // (e.g., by the URL param effect in a previous render cycle).
          if (!prevLoginData.username) {
            return { ...prevLoginData, username: rememberedUsername };
          }
          return prevLoginData; // Keep existing username
        });
        setRememberMe(true);
      }
    }
  }, [searchParams]); // Runs if searchParams object identity changes.

  useEffect(() => {
    if (
      profileDataState.isSuccess &&
      profileDataState.currentUserData.isSuccess
    ) {
      const rolesArray = [];
      Array.from(profileDataState.currentUserData.result.features).filter(
        (i) => {
          rolesArray.push(i.feature);
        }
      );
      localStorage.setItem("rolesArray", rolesArray);
      localStorage.setItem(
        "userData",
        JSON.stringify(profileDataState.currentUserData.result)
      );

      dispatch(clearProfileDataSliceState());
      navigate("/home");
    }
  }, [profileDataState, navigate, dispatch]);

  useEffect(() => {
    if (loginState.isSuccess === true) {
      if (loginState.result.message === "success") {
        localStorage.setItem(
          "dealersList",
          JSON.stringify(loginState.result.result.branchResult)
        );
        localStorage.setItem("rbacToken", loginState.result.result.tokenIs);
        localStorage.setItem(
          "currentDealerId",
          loginState.result.result.currentBranch
        );
        const token = localStorage.getItem("rbacToken");
        if (!token) {
          // This case should ideally not happen if login was successful
          // but good to have a guard.
          dispatch(setShowMessage("Login successful but token not found."));
          return;
        } else {
          dispatch(getProfileData(token));
          dispatch(setShowMessage("Welcome to Vehicle Management System"));
        }
      } else if (loginState.result.message !== "success") {
        // Specific messages for known non-success cases
        if (loginState.result.message === "Unauthorized access") {
          dispatch(setShowMessage("Unauthorized access"));
        } else {
          // Assuming other non-success messages mean wrong credentials
          dispatch(setShowMessage("Credentials are wrong"));
        }
      } else {
        // Fallback for unexpected success=true but non-standard message
        dispatch(setShowMessage("Login attempt resulted in an unknown state."));
      }
      dispatch(clearLoginState());
    } else if (loginState.isError === true) {
      const errorMessage = loginState.error?.data?.message || loginState.error?.message || "Login failed. Please try again.";
      dispatch(setShowMessage(errorMessage));
      dispatch(clearLoginState()); // Clear login state after showing error to prevent re-showing on re-renders
    }
  }, [loginState, dispatch, navigate]); // Added navigate as it's used in profileDataState effect, good practice if similar pattern is expected

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setLoginData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const downloadAndroidApp = () => {
    console.log("Downloading Android...");

    const buildTag = process.env.REACT_APP_BUILD_TAG;
    const buildNumber = process.env.REACT_APP_BUILD_NUMBER;

    if (!buildTag || !buildNumber) {
      console.error("Missing build tag or build number in environment variables.");
      window.alert("Download unavailable. Please try again later.");
      return;
    }

    const androidApkUrl = `/app-${buildTag}-${buildNumber}.apk`;

    const anchor = document.createElement("a");
    anchor.style.display = "none";
    anchor.href = androidApkUrl;

    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    console.log("Download triggered for:", androidApkUrl);
  };

  const downloadiOsApp = async () => {
    console.log("Attempt to download iOS app...");
    window.alert("The iOS version of the app is coming soon. Stay tuned!");
  };

  const handleReset = () => {
    navigate("/reset-password", { state: { email: loginData.username } })
  }
  const handleLogin = async (event) => {
    event.preventDefault();

    // Get location function
    const getLocation = () =>
      new Promise((resolve) => {
        if (!navigator.geolocation) {
          resolve(null); // Fallback if not supported
        } else {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              });
            },
            (error) => {
              console.warn("Geolocation error:", error.message);
              resolve(null); // Don't block login if location fails
            },
            { timeout: 10000 }
          );
        }
      });

    // Fetch location
    const userLocation = await getLocation();

    const { username, password } = loginData;

    // Validate form
    if (!username?.trim() || !password?.trim()) {
      dispatch(setShowMessage("Please fill all the fields"));
      return;
    }

    // Merge location into loginData
    const loginPayload = {
      ...loginData,
      location: userLocation,
    };

    setLoading(true);
    dispatch(getLoginUser(loginPayload))
      .finally(() => {
        setLoading(false);
      });

    // Remember username if checked
    if (rememberMe) {
      localStorage.setItem("rememberedUsername", username.trim());
    } else {
      localStorage.removeItem("rememberedUsername");
    }
  };

  useEffect(() => {
    let locationInterval;

    const startLocationUpdate = () => {
      const updateLocation = async () => {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(async (position) => {
            const location = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };

            const userDataString = localStorage.getItem("userData");
            const token = localStorage.getItem("rbacToken");

            if (userDataString && token) {
              const userData = JSON.parse(userDataString);
              if (userData?.user_id) {
                try {
                  const apiEndpoint = `${process.env.REACT_APP_NODE_URL}/api/login/update-user-location`;
                  await axios.post(apiEndpoint, {
                    user_id: userData.user_id,
                    location: location,
                  });
                  console.log("Location updated successfully:", location);
                } catch (err) {
                  console.error("Failed to update location on the server:", err);
                }
              }
            }
          }, (err) => {
            console.warn("Could not get geolocation:", err.message);
          });
        } else {
          console.warn("Geolocation is not supported by this browser.");
        }
      };
      updateLocation();
      locationInterval = setInterval(updateLocation, 15 * 60 * 1000);
    };

    if (localStorage.getItem("rbacToken")) {
      startLocationUpdate();
    }

    return () => {
      if (locationInterval) {
        clearInterval(locationInterval);
      }
    };
  }, []);

  return (
    <div className="login-page">
      {" "}
      {/* Flex container for centering */}
      <div className="form-wrapper">
        {" "}
        {/* This element is centered by .login-page */}
        {/* Form Card */}
        <div className="form-card">
          {/* Build Info Icon */}
          <div className="build-info-container">
            <FaInfoCircle
              className="build-info-icon"
              onMouseEnter={() => setShowBuildInfo(true)}
              onMouseLeave={() => setShowBuildInfo(false)}
            />
            {showBuildInfo && (
              <div className="build-info-tooltip">
                <h4>Build Information</h4>
                <p><strong>Build ID:</strong> {buildInfo.buildId}</p>
                <p><strong>Branch:</strong> {buildInfo.branch}</p>
                <p><strong>Build Date:</strong> {buildInfo.buildDate}</p>
                <p><strong>Version:</strong> {buildInfo.version}</p>
              </div>
            )}
          </div>

          {/* Logo and Dealership Name Container */}
          <div className="login-logo-container">
            {/* Outer Circle */}
            <div className="logo-outer-circle">
              {/* Inner Circle */}
              <div className="logo-inner-circle">
                <img
                  src={agencyData.angencyLogo || defaultLogo}
                  alt="Logo"
                  className="login-logo"
                />
              </div>
            </div>
            {/* Dealership Name Below Circle */}
            <h3 className="dealership-name">
              {agencyData.agencyName || "Balkrushna CRM"}
            </h3>
          </div>

          <hr />
          {/* Sign in Heading */}
          <h2>Sign in to your account</h2>

          {/* Login Form */}
          <form onSubmit={handleLogin}>
            {" "}
            {/* Use onSubmit on the form */}
            {/* Email */}
            <div className="form-group">
              <label htmlFor="username">Email address</label> {/* Changed htmlFor to username to match id/name */}
              <div className="input-container">
                {/* SVG Email Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="icon input-icon"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 12a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 10-2.636 6.364M16.5 12V8.25"
                  />
                </svg>
                <input
                  type="username" // Changed type to text, as it can be email or mobile. 'username' is not a standard type.
                  id="username"
                  name="username"
                  placeholder="Enter your email or mobile" // Updated placeholder
                  value={loginData.username}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Password */}
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-container">
                {/* SVG Lock Icon */}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="icon input-icon"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                  />
                </svg>
                <input
                  type="password"
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  value={loginData.password}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
            {/* Options */}
            <div className="form-options">
              <div className="rememberMe">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                />
                <label htmlFor="rememberMe">Remember me</label>
              </div>
              <button type="button" onClick={handleReset} className="forgot-password"> {/* Changed to type="button" */}
                Forgot password?
              </button>
            </div>
            {/* Submit Button */}
            <button type="submit" className="signin-button" disabled={loading}> {/* Added disabled state for loading */}
              {loading ? (
                "Signing in..."
              ) : (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="icon button-icon"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9"
                    />
                  </svg>
                  Sign in
                </>
              )}
            </button>
          </form>

          {/* --- App Download Section START --- */}
          <div className="app-download-section">
            <h6>Download our Mobile App</h6>
            <div className="app-store-buttons">
              {/* Android Button */}
              <button
                onClick={downloadAndroidApp}
                rel="noopener noreferrer"
                className="app-store-button android-button"
              >
                <FaAndroid className="store-icon" />
              </button>
              {/* Apple/iOS Button */}
              <button
                onClick={downloadiOsApp}
                rel="noopener noreferrer"
                className="app-store-button apple-button"
              >
                <FaApple className="store-icon" />
              </button>
            </div>
          </div>
          {/* --- App Download Section END --- */}
        </div>{" "}
        {/* End of form-card */}
      </div>{" "}
      {/* End of form-wrapper */}
    </div> // End of login-page
  );
}

export default LoginPage;